import { get } from "es-toolkit/compat"
import { useState } from "react"

export interface PostalCodeSearchResult {
  prefecture?: string
  prefectureKana?: string
  city?: string
  cityKana?: string
  town?: string
  townKana?: string
  district?: string
  districtKana?: string
}

export interface UsePostalCodeSearchOptions {
  onSuccess?: (result: PostalCodeSearchResult) => void
  onError?: (error: string) => void
}

export function usePostalCodeSearch(options?: UsePostalCodeSearchOptions) {
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const searchAddress = async (upperZip: string, lowerZip: string) => {
    const zip = `${upperZip}${lowerZip}`.replace("-", "")

    if (zip.length !== 7) {
      const errorMessage = "郵便番号（上3桁と下4桁）を正しく入力してください"
      setError(errorMessage)
      options?.onError?.(errorMessage)
      return null
    }

    setIsSearching(true)
    setError(null)

    try {
      // jp-postal-code-api を使用
      const response = await fetch(`https://jp-postal-code-api.ttskch.com/api/v1/${zip}.json`)

      if (response.ok) {
        const data = await response.json()
        const result = get(data, "addresses[0].ja")
        const kanaResult = get(data, "addresses[0].kana")

        if (result) {
          const addressData: PostalCodeSearchResult = {
            prefecture: result.prefecture,
            prefectureKana: kanaResult?.prefecture,
            city: result.address1,
            cityKana: kanaResult?.address1,
            town: result.address2 ?? "",
            townKana: kanaResult?.address2 ?? "",
            district: `${result.address3 ?? ""}${result.address4 ?? ""}`,
            districtKana: `${kanaResult?.address3 ?? ""}${kanaResult?.address4 ?? ""}`,
          }

          options?.onSuccess?.(addressData)
          return addressData
        }
      }

      const errorMessage = "該当する住所が見つかりませんでした"
      setError(errorMessage)
      options?.onError?.(errorMessage)
      return null
    } catch (err) {
      console.error("Address search error:", err)
      const errorMessage = "住所の検索に失敗しました"
      setError(errorMessage)
      options?.onError?.(errorMessage)
      return null
    } finally {
      setIsSearching(false)
    }
  }

  return {
    searchAddress,
    isSearching,
    error,
    clearError: () => setError(null),
  }
}
